// PURPOSE: Provider-neutral authenticated application session. CONTRACT: no local impersonation/runtime bypass. DEPENDS: public auth config, OIDC PKCE adapter, native membership endpoint. CEILING: provider authenticates; API authorizes.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useToast } from './toast';
import { ApiClient, errorMessage } from './api';
import type { AuthConfig, Membership } from './types';
export { oidcAdapter } from './auth-session';
import { oidcAdapter, type AuthAdapter } from './auth-session';
export type { AuthAdapter } from './auth-session';
interface Session { api: ApiClient; me: Membership; signOut(): Promise<void> }
const SessionContext = createContext<Session | null>(null);
export function useSession() { const value = useContext(SessionContext); if (!value) throw new Error('Authenticated session missing'); return value; }
let redirectStarted = false;
let bootstrapPromise: Promise<{ adapter: AuthAdapter | null; me: Membership | null; error?: string }> | undefined;
function bootstrapSession() {
  // React StrictMode can replay effects. An authorization code must be consumed exactly once.
  return bootstrapPromise ||= (async () => {
    let adapter: AuthAdapter | null = null;
    try {
      const response = await fetch('/api/auth/config'); if (!response.ok) throw new Error('Cannot reach sign-in configuration. Check the server connection.');
      const config: AuthConfig = await response.json(); adapter = oidcAdapter(config);
      const callback = location.pathname === '/auth/callback';
      if (callback) await adapter.completeSignIn();
      const token = await adapter.getToken(); const me = token ? await new ApiClient(() => adapter!.getToken()).me() : null;
      if (me) me.email = await adapter.getEmail?.() || null;
      if (!me) {
        if (token || callback) return { adapter, me: null, error: callback && !token ? "Sign-in did not complete. Try again." : "This account has no workspace membership yet. Ask an admin to add you." };
        if (!redirectStarted) { redirectStarted = true; await adapter.signIn(); }
      }
      return { adapter, me };
    } catch (error) { return { adapter, me: null, error: errorMessage(error) }; }
  })();
}
export function SessionProvider({ children }: { children: ReactNode }) {
  const [adapter, setAdapter] = useState<AuthAdapter | null>(null); const [me, setMe] = useState<Membership | null>(null);
  const [loading, setLoading] = useState(true); const [error, setError] = useState('');
  const api = useMemo(() => adapter ? new ApiClient(() => adapter.getToken()) : null, [adapter]);
  useEffect(() => { let alive = true; bootstrapSession().then(result => { if (alive) { setAdapter(result.adapter); setMe(result.me); setError(result.error || ''); setLoading(false); } }); return () => { alive = false; }; }, []);
  if (loading || (!me && !error)) return <div className="auth-loading"><img className="brand-image" src="/brand/mark.png" alt="" width="24" height="24" /><p>Opening your workspace…</p></div>;
  if (!me || !api || !adapter) return <main className="signin">
    <section className="signin-story">
      <div className="wordmark"><img className="brand-image" src="/brand/mark.png" alt="" width="24" height="24" /> VELOX <small>ERP</small></div>
      <div><h1>From the first conversation<br />to the final handoff.</h1></div>
    </section>
    <section className="signin-panel"><div>
      <h2>Can't open your workspace</h2>
      <div role="alert" className="error-box">{error}</div>
      <div className="inline-actions">
        <button className="text-button" onClick={() => location.reload()}>Retry</button>
        <button className="text-button" disabled={!adapter} onClick={() => adapter?.signOut().catch(e => setError(errorMessage(e)))}>Switch account</button>
      </div>
    </div></section>
  </main>;
  return <SessionContext.Provider value={{ api, me, signOut: () => adapter.signOut() }}>{children}<SessionExpiryWarning adapter={adapter} /></SessionContext.Provider>;
}
export { SessionContext };

// The adapter reads the stored OIDC user, so background renewal also clears this warning.
export function SessionExpiryWarning({adapter}:{adapter:AuthAdapter}) {
 const {show,update,dismiss}=useToast();
 useEffect(()=>{
  if(!adapter.getExpiresAt)return;
  let alive=true,reading=false,renewing=false,failed=false,id:number|undefined,seenExpiry:number|null=null;
  const clear=()=>{if(id!==undefined)dismiss(id);id=undefined;};
  const signIn={label:'Sign in again',href:'/',run:()=>adapter.signIn()};
  const renew=async()=>{
   if(renewing)return;renewing=true;
   try {if(!adapter.renew)throw new Error('Unavailable');await adapter.renew();if(alive){clear();failed=false;}}
   catch {if(alive && id!==undefined){failed=true;update(id,{message:'Could not renew. Sign in again.',action:signIn});}}
   finally {renewing=false;}
  };
  const check=async()=>{
   if(reading || renewing)return;reading=true;
   try {
    const expiry=await adapter.getExpiresAt!();if(!alive)return;
    if(expiry!==seenExpiry){clear();failed=false;seenExpiry=expiry;}
    if(expiry===null || !Number.isFinite(expiry)){clear();return;}
    const seconds=Math.max(0,Math.ceil(expiry-Date.now()/1000));
    if(seconds>120){clear();return;}
    if(failed)return;
    const message=seconds>0?`You'll be signed out in ${Math.floor(seconds/60)}:${String(seconds%60).padStart(2,'0')}`:'Your session has expired.';
    const options={message,persistent:true,action:seconds>0?{label:'Stay signed in',run:renew}:signIn};
    if(id===undefined)id=show(options);else update(id,options);
   } catch { /* An unavailable store supplies no reliable expiry; API authentication still applies. */ }
   finally {reading=false;}
  };
  void check();const timer=window.setInterval(()=>void check(),1000);
  return()=>{alive=false;window.clearInterval(timer);clear();};
 },[adapter,show,update,dismiss]);
 return null;
}
